import {
  Table,
  TableBody,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Box,
  Typography,
} from '@mui/material';
import {
  Edit as EditIcon,
  DeleteOutline as DeleteIcon,
  PictureAsPdf as PrintIcon,
} from '@mui/icons-material';
import {
  GeneralTableBox,
  HeadTableCell,
  RowTableCell,
  TableBodySkeleton,
  NoDataSkeleton,
  PaginationTable,
  StatusChip,
} from '../../../common/components';
import { formatCurrency, formatDate } from '../../../utils/formatUtils';
import { parseAmountInput } from '../utils/paymentOrdersUtils';

const statusConfigMap = {
  pending: { label: 'Pendiente', color: 'warning' },
  paid: { label: 'Pagada', color: 'success' },
  cancelled: { label: 'Anulada', color: 'error' },
};

const normalizeStatusValue = (status) => {
  if (!status) return null;

  if (typeof status === 'string') {
    const trimmed = status.trim();
    if (!trimmed) return null;
    return trimmed.split('.').pop().toLowerCase();
  }

  if (typeof status === 'object' && status.value) {
    return normalizeStatusValue(status.value);
  }

  return String(status).split('.').pop().toLowerCase();
};

const resolveStatusChip = (status) => {
  const normalized = normalizeStatusValue(status);
  if (!normalized) {
    return { label: 'Sin estado', color: 'info' };
  }

  const config = statusConfigMap[normalized];
  if (config) {
    return config;
  }

  const label =
    normalized.length > 1
      ? normalized.charAt(0).toUpperCase() + normalized.slice(1)
      : normalized.toUpperCase();

  return {
    label,
    color: 'info',
  };
};

const PaymentOrdersTable = ({
  orders = [],
  isLoading = false,
  isFetching = false,
  page,
  totalPages,
  totalCount = 0,
  rowsPerPage = 10,
  onPageChange,
  onRowsPerPageChange,
  legalEntityMap = {},
  onEdit,
  onDelete,
  onPrint,
  disableActions = false,
}) => {
  const normalizeAmount = (value) => {
    if (value === null || value === undefined) return 0;
    if (typeof value === 'number') return Number.isNaN(value) ? 0 : Math.abs(value);
    const parsed = parseAmountInput(value);
    return parsed === null ? 0 : Math.abs(parsed);
  };

  const actionsDisabled = disableActions || isFetching;

  return (
    <>
      <GeneralTableBox>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <HeadTableCell>Fecha</HeadTableCell>
              <HeadTableCell>N° orden</HeadTableCell>
              <HeadTableCell>Beneficiario</HeadTableCell>
              <HeadTableCell align="center">Estado</HeadTableCell>
              <HeadTableCell>Observaciones</HeadTableCell>
              <HeadTableCell align="right">Total</HeadTableCell>
              <HeadTableCell align="center">Acciones</HeadTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableBodySkeleton />
            ) : orders && orders.length > 0 ? (
              orders.map((order) => {
                const totalAmountValue = normalizeAmount(order.total_amount);
                const movementsSumValue = normalizeAmount(
                  order.bank_movement_summary ?? order.total_amount
                );
                const hasMismatch = Math.abs(totalAmountValue - movementsSumValue) >= 0.01;
                const statusChip = resolveStatusChip(order.status);

                return (
                  <TableRow key={order.uuid} hover>
                    <RowTableCell>{formatDate(order.emission_date)}</RowTableCell>
                    <RowTableCell>{order.order_number ?? '-'}</RowTableCell>
                    <RowTableCell>{legalEntityMap[order.uuid_legal_entity] || '-'}</RowTableCell>
                    <RowTableCell align="center">
                      <StatusChip label={statusChip.label} color={statusChip.color} size="small" />
                    </RowTableCell>
                    <RowTableCell>
                      {order.observations || order.applied_to_description || '-'}
                    </RowTableCell>
                    <RowTableCell
                      align="right"
                      sx={{
                        color: hasMismatch ? 'error.main' : 'inherit',
                        fontWeight: hasMismatch ? 600 : 'inherit',
                      }}
                    >
                      <Box
                        sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}
                      >
                        <Typography component="span" variant="body2">
                          {formatCurrency(totalAmountValue, 2)}
                        </Typography>
                        {hasMismatch && (
                          <>
                            <Typography component="span" variant="caption" color="error">
                              Movimientos: {formatCurrency(movementsSumValue, 2)}
                            </Typography>
                            <Typography component="span" variant="caption" color="error">
                              Diferencia: {formatCurrency(
                                Math.abs(totalAmountValue - movementsSumValue),
                                2
                              )}
                            </Typography>
                          </>
                        )}
                      </Box>
                    </RowTableCell>
                    <RowTableCell align="center">
                      <Tooltip title="Editar">
                        <span>
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => onEdit(order.uuid)}
                            disabled={actionsDisabled}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Guardar e imprimir">
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => onPrint(order.uuid)}
                            disabled={actionsDisabled}
                          >
                            <PrintIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Eliminar orden">
                        <span>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => onDelete(order)}
                            disabled={actionsDisabled}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </RowTableCell>
                  </TableRow>
                );
              })
            ) : (
              <NoDataSkeleton message="No se encontraron órdenes de pago para el período seleccionado." />
            )}
          </TableBody>
        </Table>
      </GeneralTableBox>

      {!!orders.length && (
        <PaginationTable
          page={page}
          totalPages={totalPages}
          totalCount={totalCount}
          rowsPerPage={rowsPerPage}
          onPageChange={onPageChange}
          onRowsPerPageChange={onRowsPerPageChange}
        />
      )}
    </>
  );
};
export default PaymentOrdersTable;
